import { useState } from "react";

interface AboutStrategyProps {
  strategyName: string;
  metrics?: {
    cagr?: number;
    sharpe?: number;
    sortino?: number;
    max_drawdown?: number;
    maxDrawdown?: number;
    volatility?: number;
    total_return?: number;
    totalReturn?: number;
    best_year?: number;
    bestYear?: number;
    worst_year?: number;
    worstYear?: number;
  };
}

interface StrategyAboutData {
  description: string;
  tickers: string;
  riskLevel: "Conservative" | "Moderate" | "Aggressive";
  riskColor: string;
  riskBg: string;
}

const STRATEGY_ABOUT: Record<string, StrategyAboutData> = {
  "TC-01": {
    description:
      "An all-weather portfolio that automatically shifts between growth assets (like stocks) and safe-haven assets (like bonds and gold) based on current market conditions. When markets are strong, it leans into growth. When trouble appears, it moves to safety.",
    tickers:
      "Stocks & Index ETFs (SPY, QQQ, IWM) · Leveraged ETFs (TQQQ, UPRO, SOXL, TECL) · Bonds (TLT, TMF, AGG, BND, IEF, SHY) · Gold & Commodities (GLD, SLV, IAU, DBC) · Volatility Hedges (UVXY, SVXY) · Sector ETFs (XLK, XLF, XLP, XLU)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-02": {
    description:
      'Uses the VIX "fear index" to decide how aggressively to invest. When fear is low (markets calm), it goes heavy into growth stocks and leveraged ETFs. When fear spikes, it automatically moves into defensive positions like bonds and cash. Think of it as a smart dial that turns risk up or down.',
    tickers:
      "Large-Cap Stocks (AAPL, MSFT, NVDA, GOOGL, AMZN, META, TSLA) · Index ETFs (SPY, QQQ, DIA, IWM) · Leveraged ETFs (TQQQ, UPRO, SOXL, FNGU, BULZ) · Bonds (TLT, TMF, AGG, BND, EDV) · Gold (GLD, IAU) · Volatility (UVXY, SVXY, SVIX, SVOL) · Sector ETFs (XLK, XLF, XLE, XLV)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-03": {
    description:
      "A market-timing strategy that looks at multiple timeframes (short, medium, long-term trends) to find the best moments to buy and sell. When all timeframes agree the market is heading up, it goes in. When signals conflict or turn negative, it steps aside into safe assets.",
    tickers:
      "Index ETFs (SPY, QQQ, IWM) · Leveraged ETFs (TQQQ, UPRO, SOXL, TECL, QLD) · Inverse ETFs (SQQQ, SH, PSQ) · Bonds (TLT, TMF, AGG, IEF, BIL) · Volatility (UVXY, VIXY, VXX) · Commodities (UGL, UCO, UNG)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-04": {
    description:
      'Combines two proven approaches: it follows the overall market trend, but within that trend, it buys assets that have temporarily dipped (mean reversion). Think of it as "buying on sale" but only during a bull market, and stepping aside during bear markets.',
    tickers:
      "Index ETFs (SPY, QQQ, IWM, IOO) · Leveraged ETFs (TQQQ, UPRO, SPXL, SOXL, TECL) · Inverse/Short ETFs (SQQQ, SPXS, SH, PSQ, TECS) · Bonds (TLT, TMF, AGG, BND, IEF, BIL) · Gold & Silver (GLD, SLV, UGL) · Volatility (UVXY, SVXY, SVIX) · Sector ETFs (XLK, XLF, XLP, XLU, XLV, XLY)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-05": {
    description:
      'A high-probability strategy focused on protecting your money first and growing it second. Uses strict rules to limit how much it can lose on any single trade while targeting consistent, steady gains. It\'s the "slow and steady wins the race" approach — but with professional-grade risk controls.',
    tickers:
      "Large-Cap Stocks (AAPL, MSFT, NVDA, GOOGL, AMZN, META, TSLA, JPM, JNJ) · Index ETFs (SPY, QQQ, DIA, IWM, VOO) · Leveraged ETFs (TQQQ, UPRO, SPXL, SOXL, TECL) · Bonds (TLT, TMF, AGG, BND, IEF, SHY, BIL) · Gold & Commodities (GLD, SLV, IAU, DBC) · Volatility (UVXY, SVXY, SVIX) · Sector ETFs (XLK, XLF, XLE, XLP, XLU, XLV, XLY)",
    riskLevel: "Conservative",
    riskColor: "text-green-400",
    riskBg: "bg-green-500/20 border-green-500/40",
  },
  "TC-06": {
    description:
      "Rotates between three main asset classes — tech stocks, treasury bonds, and gold — using leverage to amplify returns. Volatility signals tell it which asset to favor at any given time. When tech is strong, it rides tech. When uncertainty rises, it shifts to bonds or gold for protection.",
    tickers:
      "Index ETFs (SPY, QQQ, DIA, IWM, VTI) · Leveraged ETFs (TQQQ, UPRO, SOXL, TECL, TMF, QLD) · Bonds (TLT, TMF, AGG, BND, IEF, SHY, HYG, BNDX) · Gold (GLD, IAU, UGL) · Volatility (UVXY, SVXY, SVOL) · Sector ETFs (XLK, XLF, XLP, XLU, XLV, XLY) · Smart Beta (SPHQ, SPMO, USMV, DGRW)",
    riskLevel: "Conservative",
    riskColor: "text-green-400",
    riskBg: "bg-green-500/20 border-green-500/40",
  },
  "TC-07": {
    description:
      "Runs two competing systems simultaneously — one that chases momentum (buying what's going up) and one that buys dips (buying what's gone down). It tracks which approach is working better in the current market and automatically shifts more money to the winning system.",
    tickers:
      "Index ETFs (SPY, QQQ, IWM, VTI, IOO) · Leveraged ETFs (TQQQ, UPRO, SPXL, SOXL, TECL, QLD) · Inverse ETFs (SPXS, SH, PSQ) · Bonds (TLT, TMF, AGG, BND, IEF, BIL, BSV) · Volatility (UVXY, SVXY, VIXY, VXX) · Sector ETFs (XLK, XLF, XLP, XLU)",
    riskLevel: "Aggressive",
    riskColor: "text-red-400",
    riskBg: "bg-red-500/20 border-red-500/40",
  },
  "TC-08": {
    description:
      "Moves money between stocks, gold, and bonds — and can also short the market when conditions deteriorate. Think of it as having a financial advisor who constantly rebalances your portfolio based on what the market is doing right now, including the ability to profit when markets fall.",
    tickers:
      "Index ETFs (SPY, QQQ) · Leveraged ETFs (TQQQ, UPRO, SOXL, QLD) · Inverse/Short ETFs (SQQQ, SPXS) · Bonds (TLT, TMF) · Gold (UGL, GLD) · Currency (UDN, UUP) · Volatility (UVXY, VXX) · Sector ETFs (XLE, XLF, XLK, XLP)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-09": {
    description:
      "Built to survive policy uncertainty, trade wars, and global tensions. Spreads investments internationally and uses hedging techniques to protect against sudden geopolitical shocks. When trade-war headlines hit, this strategy is designed to hold steady instead of panicking.",
    tickers:
      "Index ETFs (SPY, QQQ, IWM, VTI, IOO) · International (EFA, EEM, EWJ, EWG, EWU, EWA, EURL, INDL) · Leveraged ETFs (TQQQ, UPRO, SOXL, TECL, TMF) · Inverse ETFs (SOXS, SPXS, SPXU, TECS, TZA) · Bonds (TLT, AGG, BND, IEF, SHY, BIL) · Gold & Commodities (GLD, SLV, IAU, DBC, DBA, CORN, WEAT) · Volatility (UVXY, SVXY, VIXY, VXX) · Sector ETFs (XLK, XLF, XLP, XLU, XLY)",
    riskLevel: "Moderate",
    riskColor: "text-yellow-400",
    riskBg: "bg-yellow-500/20 border-yellow-500/40",
  },
  "TC-10": {
    description:
      "The most aggressive strategy in the collection. Waits for the market to drop significantly, then buys leveraged tech ETFs at a discount. High risk, high reward — designed for investors who can stomach big swings and want maximum upside when markets recover from dips.",
    tickers:
      "Index ETFs (SPY, QQQ) · Leveraged ETFs (TQQQ, TECL, SPXL) · Bonds & Cash (TLT, BIL, SHY) · Gold (GLD) · Volatility (UVXY, VXX) · Currency (UUP)",
    riskLevel: "Aggressive",
    riskColor: "text-red-400",
    riskBg: "bg-red-500/20 border-red-500/40",
  },
};

function getStrategyCode(name: string): string | null {
  const match = name.match(/^TC-\d+/);
  return match ? match[0] : null;
}

function formatPercent(val: number | undefined): string {
  if (val === undefined || val === null) return "N/A";
  return `${val >= 0 ? "+" : ""}${val.toFixed(1)}%`;
}

function formatLargePercent(val: number | undefined): string {
  if (val === undefined || val === null) return "N/A";
  return `+${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}%`;
}

export function AboutStrategy({ strategyName, metrics }: AboutStrategyProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const code = getStrategyCode(strategyName);
  if (!code) return null;

  const about = STRATEGY_ABOUT[code];
  if (!about) return null;

  // Normalize metric field names (component may receive camelCase or snake_case)
  const cagr = metrics?.cagr;
  const sharpe = metrics?.sharpe;
  const maxDrawdown = metrics?.max_drawdown ?? metrics?.maxDrawdown;
  const totalReturn = metrics?.total_return ?? metrics?.totalReturn;
  const bestYear = metrics?.best_year ?? metrics?.bestYear;
  const worstYear = metrics?.worst_year ?? metrics?.worstYear;

  // Compute drawdown dollar impact on $10k
  const ddDollars =
    maxDrawdown !== undefined
      ? (10000 * (1 - Math.abs(maxDrawdown) / 100)).toLocaleString(undefined, {
          maximumFractionDigits: 0,
        })
      : null;

  return (
    <div className="glass-card mb-6 overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-surface-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-500/20 flex items-center justify-center shrink-0">
            <svg
              className="w-5 h-5 text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-surface-100">
            About This Strategy
          </h3>
        </div>
        <svg
          className={`w-5 h-5 text-surface-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Body — collapsible */}
      {isExpanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-5 border-t border-surface-700/50">
          {/* What This Strategy Does */}
          <div className="pt-5">
            <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-2">
              What This Strategy Does
            </h4>
            <p className="text-surface-300 text-sm leading-relaxed">
              {about.description}
            </p>
          </div>

          {/* How Is It Traded */}
          <div>
            <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-2">
              How Is It Traded?
            </h4>
            <div className="flex items-start gap-3 bg-surface-800/40 rounded-lg p-3 border border-surface-700/40">
              <svg
                className="w-5 h-5 text-green-400 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-surface-300 text-sm leading-relaxed">
                <span className="text-surface-100 font-medium">
                  Fully automated.
                </span>{" "}
                Once you invest, trades are executed automatically at{" "}
                <span className="text-green-400 font-medium">3:40 PM EST</span>{" "}
                every market day — no manual trades required. The strategy
                monitors conditions and rebalances your portfolio for you.
              </p>
            </div>
          </div>

          {/* What It Trades */}
          <div>
            <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-2">
              What It Trades
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {about.tickers.split(" · ").map((group) => {
                const match = group.match(/^(.+?)\s*\((.+)\)$/);
                if (!match) return null;
                const [, label, symbols] = match;
                return (
                  <div
                    key={label}
                    className="bg-surface-800/60 rounded-lg px-3 py-2 border border-surface-700/40"
                  >
                    <span className="text-xs font-medium text-surface-400 block mb-1">
                      {label}
                    </span>
                    <span className="text-xs text-surface-300">
                      {symbols}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Risk Level */}
          <div>
            <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-2">
              Risk Level
            </h4>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${about.riskBg} ${about.riskColor}`}
              >
                {about.riskLevel}
              </span>
              {ddDollars && maxDrawdown !== undefined && (
                <span className="text-surface-400 text-sm">
                  Worst-case historical drawdown was{" "}
                  <span className="text-surface-200 font-medium">
                    -{Math.abs(maxDrawdown).toFixed(1)}%
                  </span>
                  , meaning a $10,000 investment could have temporarily dropped
                  to ~${ddDollars} at its lowest point.
                </span>
              )}
            </div>
          </div>

          {/* Performance Snapshot */}
          <div>
            <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-2">
              Performance Snapshot
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {cagr !== undefined && (
                <div className="bg-surface-800/40 rounded-lg p-3 border border-surface-700/40">
                  <span className="text-xs text-surface-400 block mb-1">
                    Average Annual Growth
                  </span>
                  <span className="text-lg font-bold text-green-400">
                    {formatPercent(cagr)}
                  </span>
                  <span className="text-xs text-surface-500 ml-2">
                    per year (vs ~10% for S&P 500)
                  </span>
                </div>
              )}
              {totalReturn !== undefined && (
                <div className="bg-surface-800/40 rounded-lg p-3 border border-surface-700/40">
                  <span className="text-xs text-surface-400 block mb-1">
                    Total Return Since Inception
                  </span>
                  <span className="text-lg font-bold text-green-400">
                    {formatLargePercent(totalReturn)}
                  </span>
                </div>
              )}
              {(bestYear !== undefined || worstYear !== undefined) && (
                <div className="bg-surface-800/40 rounded-lg p-3 border border-surface-700/40">
                  <span className="text-xs text-surface-400 block mb-1">
                    Best / Worst Year
                  </span>
                  <span className="text-sm">
                    {bestYear !== undefined && (
                      <span className="text-green-400 font-bold">
                        {formatPercent(bestYear)}
                      </span>
                    )}
                    {bestYear !== undefined && worstYear !== undefined && (
                      <span className="text-surface-500 mx-2">·</span>
                    )}
                    {worstYear !== undefined && (
                      <span
                        className={`font-bold ${worstYear >= 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {formatPercent(worstYear)}
                        {worstYear >= 0 && (
                          <span className="text-xs text-surface-400 font-normal ml-1">
                            (still positive)
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {sharpe !== undefined && (
                <div className="bg-surface-800/40 rounded-lg p-3 border border-surface-700/40">
                  <span className="text-xs text-surface-400 block mb-1">
                    Sharpe Ratio
                  </span>
                  <span className="text-lg font-bold text-surface-100">
                    {sharpe.toFixed(2)}
                  </span>
                  <span className="text-xs text-surface-500 ml-2">
                    Return per unit of risk (above 1.0 is good, above 2.0 is
                    excellent)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
